import classNames from 'classnames';
import React from 'react';
import LazyLoad from 'react-lazyload';
import * as moment from 'moment';
import { sizeDisplay, PathUtils } from '../utils';
import './file-group.scss';
import audioLogo from '../assets/img/files/audio.png';
import genericLogo from '../assets/img/files/generic.png';
import docLogo from '../assets/img/files/doc.png';
import imageLogo from '../assets/img/files/image.png';
import pdfLogo from '../assets/img/files/pdf.png';
import pptLogo from '../assets/img/files/ppt.png';
import txtLogo from '../assets/img/files/txt.png';
import videoLogo from '../assets/img/files/video.png';
import xlsLogo from '../assets/img/files/xls.png';
import xmlLogo from '../assets/img/files/xml.png';
import zipLogo from '../assets/img/files/zip.png';
import { FileObject } from '../dff-analyse/dff-analyse';
import { useSelector } from 'react-redux';
import { baseSelector } from '../store/selectors';

export interface FileGroupProps {
  // files: File[];
  files: FileObject[];
  mount: string;
  activeFile?: FileObject;
  handleCheck: (
    e: React.ChangeEvent<HTMLInputElement>,
    f: FileObject,
    files: FileObject[],
  ) => void;
  handleClickCheckBox: (
    e: React.MouseEvent<any>,
    f: FileObject,
    files: FileObject[],
  ) => void;
  handleRightClick: (
    e: React.MouseEvent<any>,
    f: FileObject,
    files: FileObject[],
  ) => void;
  handleClearClick: (
    e: React.MouseEvent<any>,
    files: FileObject[],
  ) => void;
  handleNewerClick: (
    e: React.MouseEvent<any>,
    files: FileObject[],
  ) => void;
  handleOlderClick: (
    e: React.MouseEvent<any>,
    files: FileObject[],
  ) => void;
}

const logo = {
  audio: audioLogo,
  generic: genericLogo,
  doc: docLogo,
  image: imageLogo,
  pdf: pdfLogo,
  ppt: pptLogo,
  txt: txtLogo,
  video: videoLogo,
  xls: xlsLogo,
  xml: xmlLogo,
  zip: zipLogo,
} as any;

export const FileGroup: React.FC<FileGroupProps> = ({
  files,
  mount,
  activeFile,
  handleCheck,
  handleClickCheckBox,
  handleRightClick,
  handleClearClick,
  handleNewerClick,
  handleOlderClick,
}) => {
  const baseUrl = useSelector(baseSelector);

  const handleCheckBox = (
    e: React.ChangeEvent<HTMLInputElement>,
    f: FileObject,
    files: FileObject[],
  ): void => {
    handleCheck(e, f, files);
  };

  const handleClick = (
    e: React.MouseEvent<any>,
    f: FileObject,
    files: FileObject[],
  ): void => {
    handleClickCheckBox(e, f, files);
  };

  const handleRightClickFile = (
    e: React.MouseEvent<HTMLLIElement>,
    f: FileObject,
    files: FileObject[],
  ): void => {
    handleRightClick(e, f, files);
  };

  const handleClearClickFile = (
    e: React.MouseEvent<any>,
    files: FileObject[],
  ): void => {
    handleClearClick(e, files);
  };

  const handleNewerClickFile = (
    e: React.MouseEvent<any>,
    files: FileObject[],
  ): void => {
    handleNewerClick(e, files);
  };

  const handleOlderClickFile = (
    e: React.MouseEvent<any>,
    files: FileObject[],
  ): void => {
    handleOlderClick(e, files);
  };

  const isImage = (file: FileObject): boolean => {
    if (file.file.contentType === 'image/svg+xml') {
      return false;
    }
    const isFileImage = /\.jpg$|\.jpeg$|\.png$|\.gif$/.test(file.file.name.toLowerCase());
    const isRawImage = /\.3fr$|\.arw$|\.cr2$|\.crw$|\.dcr$|\.dng$|\.erf$|\.fff$|\.gpr$|\.iiq$|\.kdc$|\.mdc$|\.mef$|\.mos$|\.mrw$|\.nef$|\.nrw$|\.orf$|\.pef$|\.raf$|\.raw$|\.rw2$|\.rwl$|\.sr2$|\.srf$|\.srw$|\.tif$|\.x3f$|\.heic$/.test(
      file.file.name.toLowerCase(),
    );
    return isFileImage || isRawImage;
  };

  const directLink = (file: FileObject): string => {
    return `/content/api/v2/mounts/${file.mountId}/files/get?path=${encodeURIComponent(file.path)}`;
  };

  const thumbnailUrl = (file: FileObject): string => {
    return `${directLink(file)}&thumb=shmedium`;
  };

  const originalUrl = (file: FileObject): string => {
    const parentPath = PathUtils.parent(file.path);
    const name = PathUtils.baseName(file.path);

    return `${baseUrl}/app/storage/${
      file.mountId
    }?path=${encodeURIComponent(parentPath)}&name=${encodeURIComponent(name)}`;
  };

  const canClear = (files: FileObject[]): boolean => {
    return files.filter(f => f.file.isSelected).length > 0
  }

  return (
    <li className="file-group">
      <div className="file-group-top-container">
      <div className="file-group-left">
          <button onClick={e => handleNewerClickFile(e, files)}>Newer</button>
          <button onClick={e => handleOlderClickFile(e, files)}>Older</button>
          <button disabled={!canClear(files)} onClick={e => handleClearClickFile(e, files)} className={classNames({ 'not-possible': !canClear(files) })}>Clear</button>
        </div>
        <div className="file-group-info">{`${sizeDisplay(files[0].file.size)} / ${
          files.length
          } files / Total size: ${sizeDisplay(
            files.length * files[0].file.size,
          )}`}
        </div>
      </div>
      <ul className="file-group-files">
        {files
          ? files.map((f, index) => (
              <li
                className={classNames('single-file', {
                  'single-file--selected': activeFile === f,
                })}
                key={`${f.file.hash}/${index}`}
                onContextMenu={e => handleRightClickFile(e, f, files)}
              >
                <div className="single-file-checkbox">
                  <input
                    type="checkbox"
                    checked={f.file.isSelected || false}
                    disabled={f.file.preventSelection}
                    onChange={e => handleCheckBox(e, f, files)}
                    onClick={e => handleClick(e, f, files)}
                  />
                </div>
                <div className="single-file-image">
                  {isImage(f) ? (
                    <div className="thumb-pic-container">
                      <LazyLoad once>
                        <img
                          src={thumbnailUrl(f)}
                          alt="Thumbnail"
                          className="thumb-pic"
                        />
                      </LazyLoad>
                    </div>
                  ) : (
                    <img
                      src={logo[f.file.fileType]}
                      className="thumb-icon"
                      alt="Thumbnail"
                    />
                  )}
                </div>
                <div className="single-file-name">
                  <a
                    href={originalUrl(f)}
                    className={classNames('file-name', {
                      'file-name--checked': f.file.isSelected,
                    })}
                    target="_blank"
                  >
                    {mount}
                    {f.path}
                  </a>
                </div>
                <div className="single-file-date">
                  <div className="file-date">
                    {moment(f.file.modified).format('lll')}
                  </div>
                </div>
              </li>
            ))
          : null}
      </ul>
    </li>
  );
};
